import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MapPin, Save, Globe, Navigation } from 'lucide-react';
import { toast } from 'sonner';

export default function GeoToleranceTab() {
  const { api } = useApp();
  const [globalTolerance, setGlobalTolerance] = useState(500);
  const [tolerances, setTolerances] = useState([]);
  const [places, setPlaces] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState('');
  const [placeTolerance, setPlaceTolerance] = useState(500);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [tolRes, placesRes] = await Promise.all([
        api.getGeoTolerances(),
        api.getBusinessPlaces()
      ]);
      
      const tols = tolRes.data || [];
      setTolerances(tols);
      
      const globalTol = tols.find(t => t.business_place_id === '__global__');
      if (globalTol) {
        setGlobalTolerance(globalTol.tolerance_meters);
      }
      
      setPlaces(placesRes.data || []);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleSaveGlobal = async () => {
    setSaving(true);
    try {
      await api.upsertGeoTolerance({
        business_place_id: '__global__',
        tolerance_meters: globalTolerance
      });
      toast.success('Global tolerance saved');
      load();
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleSavePlaceTolerance = async () => {
    if (!selectedPlace) {
      toast.error('Select a location');
      return;
    }
    setSaving(true);
    try {
      await api.upsertGeoTolerance({
        business_place_id: selectedPlace,
        tolerance_meters: placeTolerance
      });
      toast.success('Location tolerance saved');
      setSelectedPlace('');
      setPlaceTolerance(500);
      load();
    } catch (e) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const getPlaceName = (placeId) => {
    if (placeId === '__global__') return 'Global Default';
    const place = places.find(p => p.id === placeId);
    return place?.name || placeId;
  };

  const locationTolerances = tolerances.filter(t => t.business_place_id !== '__global__');

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Global Tolerance */}
      <Card className="border border-border/60 shadow-sm">
        <CardHeader className="pb-2 px-3 sm:px-6">
          <CardTitle className="text-sm sm:text-base font-bold text-slate-900 flex items-center gap-2">
            <Globe className="w-4 h-4" /> Global Check-in Tolerance
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6">
          <p className="text-xs text-slate-500 mb-4">
            Default max distance (meters) for check-in/check-out. Locations can override.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
            <div className="flex-1 sm:max-w-xs">
              <Label>Tolerance (meters)</Label>
              <Input
                data-testid="global-tolerance-input"
                type="number"
                min={0}
                max={10000}
                value={globalTolerance}
                onChange={e => setGlobalTolerance(parseInt(e.target.value) || 0)}
              />
            </div>
            <Button
              data-testid="save-global-tolerance-btn"
              onClick={handleSaveGlobal}
              disabled={saving}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Save className="w-4 h-4" /> Save
            </Button>
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
            <Navigation className="w-3.5 h-3.5" />
            <span>Current default: {globalTolerance}m radius</span>
          </div>
        </CardContent>
      </Card>

      {/* Per-Location Tolerance */}
      <Card className="border border-border/60 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Location-Specific Tolerances
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-500 mb-4">
            Set custom check-in radius for specific locations. These will override the global default.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end mb-6">
            <div>
              <Label>Location</Label>
              <Select value={selectedPlace} onValueChange={setSelectedPlace}>
                <SelectTrigger data-testid="location-tolerance-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {places.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name} ({p.city})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tolerance (meters)</Label>
              <Input
                data-testid="place-tolerance-input"
                type="number"
                min={0}
                max={10000}
                value={placeTolerance}
                onChange={e => setPlaceTolerance(parseInt(e.target.value) || 0)}
              />
            </div>
            <Button
              data-testid="save-place-tolerance-btn"
              onClick={handleSavePlaceTolerance}
              disabled={saving || !selectedPlace}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Save className="w-4 h-4" /> Set Tolerance
            </Button>
          </div>

          {locationTolerances.length > 0 && (
            <div className="border-t border-slate-100 pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Configured Location Tolerances</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {locationTolerances.map(tol => (
                  <div key={tol.id || tol.business_place_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{getPlaceName(tol.business_place_id)}</p>
                      <p className="text-xs text-slate-500">Custom tolerance</p>
                    </div>
                    <Badge variant="secondary" className="text-sm">
                      {tol.tolerance_meters}m
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {places.length === 0 && (
            <p className="text-xs text-amber-600">
              No business locations found. Add locations in the "Business Locations" section to configure per-location tolerances.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
